import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { BranchAndWithholdCommentService } from './branch-and-withhold-comment.service';

@Component({
    selector: 'gtw-branch-and-withhold-comment',
    templateUrl: './branch-and-withhold-comment.component.html',
    styleUrls: ['./branch-and-withhold-comment.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BranchAndWithholdCommentComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  baseURLs: any = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '' };

  filterParams: any = {};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};
  gridData: any = {};

  // STANDARD COMPONENT PROPERTIES
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  modalTitle = "Comments";
  title = "Comments";

  // Form data
  COMMENTS: string = '';
  OLD_COMMENTS: string = '';
  allComments: any[] = [];
  ReturnAsFiled: boolean = false;
  is_locked: any = null;

  private initComplete = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private commentService: BranchAndWithholdCommentService,
    private snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.initComplete = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService.USER_SETTINGS = header.userSettingsData || {};
    this.appService.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : row;
    this.gridData = gridPayloadFromActiveScreen(activeScreen) || {};
  }

  init(): void {
    // Wait for ALL required inputs before loading data.
    // Inputs arrive asynchronously via attribute setters in DOM order:
    // base-urls → header-data → row-data → grid-data → filter-params
    // We must wait for filterParams to be set, otherwise getAllComments()
    // and checkLockStatus() will be called with missing tax_year/scenario.
    if (this.GENERAL_CONFIG?.base_url
        && this.rowData && Object.keys(this.rowData).length > 0
        && this.filterParams && Object.keys(this.filterParams).length > 0) {
      if (!this.initComplete) {
        this.initComplete = true;
        this.setupEntityData();
        this.checkLockStatus();
        this.getAllComments();
      }
      this.cdr.detectChanges();
    } else {
      // Still trigger change detection to render basic structure
      this.cdr.detectChanges();
    }
  }

  setupEntityData(): void {
    // Extract entity information from rowData
    if (this.rowData) {
      // Entity data setup if needed
    }
  }

  checkLockStatus(): void {
    if (!this.GENERAL_CONFIG?.base_url || !this.filterParams?.scenario || !this.globalService?.globalParams?.jcd_key) return;

    const lockparams = {
      "action_code": 'n10cpv',
      "p_scenario": this.filterParams.scenario,
      "p_jcd_key": this.globalService.globalParams.jcd_key
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      lockparams,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess == '1' && response.jsonObject) {
          this.is_locked = response.jsonObject;
          this.ReturnAsFiled = (this.is_locked != undefined && this.is_locked != null && this.is_locked[0]?.LOCK_STATUS == 'Y');
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  getAllComments(): void {
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData) return;
    
    // Only check gridData label if gridData exists, otherwise proceed (for backward compatibility)
    if (this.gridData?.colData?.label && this.gridData.colData.label !== "Comments") {
      return;
    }
    
    let uniqueId = this.rowData.OBJECT_ID?.split('_')[0] || '';
    if (!uniqueId) return;
    
    const params: any = {
      ...this.filterParams,  // Include all filter params (filing_key, parent_me_string, etc.)
      "action_code": '8n9er0',
      screen_key: this.globalService?.globalParams?.screen_key,
      object_id: uniqueId,
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.globalService?.globalParams?.jcd_key
    };
    // Only include issue_key if defined (prevents "undefined" string in query params)
    if (this.rowData.ISSUE_KEY != null) {
      params.issue_key = this.rowData.ISSUE_KEY;
    }

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.allComments = response?.jsonObject || [];
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading comments:', error);
      }
    );
  }

  save(): void {
    if (this.isSaveClicked) return;

    if (!this.COMMENTS || this.COMMENTS.trim() === '') {
      this.gtwSnackbar.openSnackBar("Please enter a valid comments", 'gtw-snackbar--red', true, 4);
      return;
    }

    if (this.COMMENTS.length > 4000) {
      this.gtwSnackbar.openSnackBar("Please comments entered cannot be more than 4000.", 'gtw-snackbar--red', true, 4);
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    let uniqueId = this.rowData.OBJECT_ID?.split('_')[0] || '';
    const logged_in_user = this.appService.USER_SETTINGS?.user?.sso_id || '';

    // Get object_id with proper fallbacks (matching branch-tax-creditable-edit pattern)
    const objectId = this.rowData.OBJECT_ID?.split('_')[0] || 
                     this.rowData.COMBINATION_KEY || 
                     this.rowData.object_id ||
                     uniqueId ||
                     '';

    const returnClobSettingsObj: any = {
      sso_id: logged_in_user,
      jcd_key: this.globalService?.globalParams?.jcd_key,
      scenario: this.filterParams.scenario,
      object_id: objectId,
      tag_key: uniqueId,
      issue_key: this.rowData.ISSUE_KEY,
      screen_key: this.globalService?.globalParams?.screen_key,
      tax_year: this.filterParams.tax_year,
      comments: this.COMMENTS.replace(/'/g, "'"),
      OPERATION_TYPE: "I"
    };

    const returnObj: any = {
      tag_key: uniqueId,
      comments: this.COMMENTS,
      OPERATION_TYPE: "I"
    };

    const defaultsChangeDetails = [returnObj];
    const otherInfo = returnClobSettingsObj;

    this.commentService.saveComments(
      this.baseURLs.api,
      returnClobSettingsObj,
      defaultsChangeDetails,
      otherInfo,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        
        // HttpClient returns the response body directly
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbar.openSnackBar(response.errorMessage, 'gtw-snackbar--red', true, 4);
        } else {
          this.gtwSnackbar.openSnackBar("Comment saved successfully", 'gtw-snackbar--green', true, 4);

          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        console.error('Error saving comment:', error);
        
        // Extract error message from various error response structures
        const errorMessage = error?.error?.errorMessage || 
                           error?.errorMessage || 
                           "Error saving comment. Please try again.";
        
        this.gtwSnackbar.openSnackBar(errorMessage, 'gtw-snackbar--red', true, 4);
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}

